(() => {
  const GLOBAL_KEY = '__jdCinematicVideo';
  const ROOT_SELECTOR = '[data-cinematic]';
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();
  const activeControllers = new Set();

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init(document);
    return;
  }

  const clamp = (value, minimum = 0, maximum = 1) =>
    Math.min(maximum, Math.max(minimum, value));

  class CinematicVideo {
    constructor(root) {
      this.root = root;
      this.sticky = root.querySelector('[data-cinematic-sticky]');
      this.frame = root.querySelector('[data-cinematic-frame]');
      this.cover = root.querySelector('[data-cinematic-cover]');
      this.host = root.querySelector('[data-cinematic-media-host]');
      this.template = root.querySelector('[data-cinematic-media-template]');
      this.audio = root.querySelector('[data-cinematic-audio]');
      this.soundButton = root.querySelector('[data-cinematic-sound]');
      this.media = null;
      this.provider = null;
      this.mediaReady = false;
      this.mediaReadyAt = 0;
      this.mediaReadyPromise = null;
      this.mediaStarted = false;
      this.mediaPlaying = false;
      this.soundActive = false;
      this.visible = false;
      this.nearViewport = false;
      this.frameRequest = 0;
      this.coverRevealTimer = 0;
      this.soundHideTimer = 0;
      this.destroyed = false;
      this.abortController = new AbortController();

      if (!this.sticky || !this.frame || !this.host || !this.template) return;

      this.bind();
      this.observe();
      this.requestUpdate();
      activeControllers.add(this);
    }

    bind() {
      const signal = this.abortController.signal;
      this.onScroll = () => this.requestUpdate();
      this.onResize = () => this.requestUpdate();
      this.onVisibility = () => {
        if (document.hidden) this.pauseAll();
        else this.requestUpdate();
      };
      this.onReducedMotion = () => this.requestUpdate();
      this.onPlayerMessage = (event) => this.handlePlayerMessage(event);
      this.onStickyClick = (event) => this.handleStickyClick(event);

      window.addEventListener('scroll', this.onScroll, { passive: true, signal });
      window.addEventListener('resize', this.onResize, { passive: true, signal });
      window.addEventListener('message', this.onPlayerMessage, { signal });
      document.addEventListener('visibilitychange', this.onVisibility, { signal });
      reducedMotion?.addEventListener?.('change', this.onReducedMotion, { signal });
      this.sticky.addEventListener('click', this.onStickyClick, { signal });
    }

    observe() {
      if (typeof IntersectionObserver !== 'function') {
        this.nearViewport = true;
        this.visible = true;
        void this.ensureMedia();
        return;
      }

      this.preloadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target !== this.root) return;
            this.nearViewport = entry.isIntersecting;
            if (entry.isIntersecting) {
              void this.ensureMedia();
              this.requestUpdate();
            }
          });
        },
        { rootMargin: '100% 0px' },
      );
      this.preloadObserver.observe(this.root);

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target !== this.root) return;
            this.visible = entry.isIntersecting;
            if (!entry.isIntersecting) {
              this.pauseMedia();
              this.stopAudio();
              this.setHeaderHidden(false);
            } else {
              this.requestUpdate();
            }
          });
        },
        { threshold: 0.01 },
      );
      this.visibilityObserver.observe(this.root);
    }

    ensureMedia() {
      if (this.mediaReadyPromise) return this.mediaReadyPromise;
      if (!this.template?.content?.childNodes.length) return Promise.resolve(null);

      this.host.appendChild(this.template.content.cloneNode(true));
      this.media = this.host.querySelector('[data-cinematic-media], .cinematic__media');
      if (!this.media) return Promise.resolve(null);

      this.provider = this.media.dataset.provider || (this.media.tagName === 'VIDEO' ? 'native' : null);
      this.mediaReadyPromise = new Promise((resolve) => {
        let settled = false;
        const markReady = () => {
          if (settled) return;
          settled = true;
          this.mediaReady = true;
          this.mediaReadyAt = window.performance?.now?.() ?? Date.now();
          if (this.provider === 'vimeo') {
            this.subscribeVimeoEvents();
            if (!this.visible) this.pauseMedia();
          }
          resolve(this.media);
        };

        if (this.provider === 'native') {
          this.media.muted = true;
          this.media.playsInline = true;
          this.media.addEventListener(
            'playing',
            () => {
              this.mediaPlaying = true;
              this.hideCover();
            },
            { signal: this.abortController.signal },
          );
          if (this.media.readyState >= 2) markReady();
          else {
            this.media.addEventListener('canplay', markReady, { once: true });
            this.media.addEventListener('error', markReady, { once: true });
          }
        } else {
          this.media.addEventListener('load', markReady, { once: true });
          this.media.addEventListener('error', markReady, { once: true });
        }

        window.setTimeout(markReady, 5000);
      });

      return this.mediaReadyPromise;
    }

    postMessage(payload) {
      try {
        this.media?.contentWindow?.postMessage(
          typeof payload === 'string' ? payload : JSON.stringify(payload),
          '*',
        );
      } catch (_error) {
        // A cross-origin player can be unavailable briefly while its iframe loads.
      }
    }

    subscribeVimeoEvents() {
      if (this.provider !== 'vimeo' || !this.media) return;
      this.postMessage({ method: 'addEventListener', value: 'play' });
      this.postMessage({ method: 'addEventListener', value: 'playing' });
      this.postMessage({ method: 'addEventListener', value: 'timeupdate' });
      this.postMessage({ method: 'addEventListener', value: 'bufferend' });
      this.postMessage({ method: 'addEventListener', value: 'pause' });
      this.postMessage({ method: 'addEventListener', value: 'ended' });
    }

    revealCoverOnPlayback() {
      if (!this.mediaStarted || !this.visible || document.hidden) return;
      window.requestAnimationFrame(() => {
        if (this.mediaStarted && this.visible && !document.hidden) this.hideCover();
      });
    }

    scheduleVimeoCoverReveal() {
      if (this.provider !== 'vimeo' || !this.cover || this.coverRevealTimer) return;
      const now = window.performance?.now?.() ?? Date.now();
      const readyAge = this.mediaReadyAt ? Math.max(0, now - this.mediaReadyAt) : 0;
      const delay = Math.max(80, 900 - readyAge);

      this.coverRevealTimer = window.setTimeout(() => {
        this.coverRevealTimer = 0;
        if (this.mediaStarted && this.visible && !document.hidden) this.hideCover();
      }, delay);
    }

    handlePlayerMessage(event) {
      if (
        this.provider !== 'vimeo' ||
        !this.media?.contentWindow ||
        event.source !== this.media.contentWindow ||
        event.origin !== 'https://player.vimeo.com'
      ) {
        return;
      }

      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (_error) {
          return;
        }
      }
      if (!data || typeof data !== 'object') return;

      if (data.event === 'ready') {
        this.mediaReady = true;
        this.mediaReadyAt = this.mediaReadyAt || (window.performance?.now?.() ?? Date.now());
        this.subscribeVimeoEvents();
        if (this.mediaStarted && this.visible && !document.hidden) this.playMedia();
        return;
      }

      if (data.event === 'play') {
        this.mediaPlaying = true;
        return;
      }

      if (data.event === 'playing' || data.event === 'bufferend') {
        this.mediaPlaying = true;
        this.revealCoverOnPlayback();
        return;
      }

      if (data.event === 'timeupdate') {
        this.mediaPlaying = true;
        const seconds = Number(data.data?.seconds ?? 0);
        if (seconds > 0) this.revealCoverOnPlayback();
        return;
      }

      if (data.event === 'pause' || data.event === 'ended') {
        this.mediaPlaying = false;
      }
    }

    playMedia({ reset = false } = {}) {
      if (!this.media || !this.mediaReady || document.hidden || !this.visible) return;
      if (this.mediaPlaying && !reset) {
        if (this.provider === 'vimeo') this.scheduleVimeoCoverReveal();
        return;
      }

      if (this.provider === 'native') {
        this.mediaPlaying = true;
        if (reset) {
          try {
            this.media.currentTime = 0;
          } catch (_error) {
            // Some streaming sources don't expose currentTime until metadata is ready.
          }
        }
        const playPromise = this.media.play?.();
        playPromise?.catch?.(() => {
          this.mediaPlaying = false;
        });
      } else if (this.provider === 'youtube') {
        this.mediaPlaying = true;
        if (reset) this.postMessage({ event: 'command', func: 'seekTo', args: [0, true] });
        this.postMessage({ event: 'command', func: 'playVideo', args: [] });
        window.setTimeout(() => {
          if (this.mediaStarted && this.visible) this.hideCover();
        }, 250);
      } else if (this.provider === 'vimeo') {
        this.mediaPlaying = true;
        this.subscribeVimeoEvents();
        if (reset) this.postMessage({ method: 'setCurrentTime', value: 0 });
        this.postMessage({ method: 'play' });
        this.scheduleVimeoCoverReveal();
      }
    }

    pauseMedia() {
      this.mediaPlaying = false;
      if (!this.media) return;
      if (this.provider === 'native') this.media.pause?.();
      else if (this.provider === 'youtube') {
        this.postMessage({ event: 'command', func: 'pauseVideo', args: [] });
      } else if (this.provider === 'vimeo') {
        this.postMessage({ method: 'pause' });
      }
    }

    resetMedia() {
      if (!this.media) return;
      this.pauseMedia();
      if (this.provider === 'native') {
        try {
          this.media.currentTime = 0;
        } catch (_error) {}
      } else if (this.provider === 'youtube') {
        this.postMessage({ event: 'command', func: 'seekTo', args: [0, true] });
      } else if (this.provider === 'vimeo') {
        this.postMessage({ method: 'setCurrentTime', value: 0 });
      }
    }

    showCover() {
      if (this.coverRevealTimer) {
        window.clearTimeout(this.coverRevealTimer);
        this.coverRevealTimer = 0;
      }
      this.cover?.classList.remove('is-hidden');
    }

    hideCover() {
      if (this.coverRevealTimer) {
        window.clearTimeout(this.coverRevealTimer);
        this.coverRevealTimer = 0;
      }
      this.cover?.classList.add('is-hidden');
    }

    setHeaderHidden(hidden) {
      document.documentElement.classList.toggle('cinematic-header-hidden', Boolean(hidden));
    }

    setSoundState(active) {
      this.soundActive = Boolean(active);
      if (!this.soundButton) return;

      this.soundButton.dataset.soundActive = String(this.soundActive);
      this.soundButton.setAttribute(
        'aria-label',
        this.soundActive ? 'Desactivar sonido' : 'Activar sonido',
      );
      const offLabel = this.soundButton.querySelector('[data-cinematic-sound-off]');
      const onLabel = this.soundButton.querySelector('[data-cinematic-sound-on]');
      if (offLabel) offLabel.hidden = this.soundActive;
      if (onLabel) {
        onLabel.hidden = !this.soundActive;
        onLabel.textContent = 'Desactivar sonido';
      }
    }

    handleStickyClick(event) {
      if (!this.audio || !this.soundButton) return;
      const clickedSoundButton = this.soundButton.contains(event.target);

      if (clickedSoundButton) {
        event.preventDefault();
        if (this.soundActive) this.muteSound();
        else void this.activateSound();
        return;
      }

      if (!this.audio.paused) this.soundButton.classList.toggle('show');
    }

    muteSound() {
      if (!this.audio) return;
      if (this.soundHideTimer) {
        window.clearTimeout(this.soundHideTimer);
        this.soundHideTimer = 0;
      }
      this.audio.muted = true;
      this.setSoundState(false);
      this.soundButton?.classList.add('show');
    }

    async activateSound() {
      if (!this.audio) return;

      if (this.soundHideTimer) {
        window.clearTimeout(this.soundHideTimer);
        this.soundHideTimer = 0;
      }

      await this.ensureMedia();

      if (this.audio.src && !this.audio.paused) {
        this.audio.muted = false;
        this.setSoundState(true);
        this.soundButton?.classList.add('show');
        this.soundHideTimer = window.setTimeout(() => {
          this.soundHideTimer = 0;
          this.soundButton?.classList.remove('show');
        }, 800);
        return;
      }

      this.mediaStarted = true;
      this.playMedia({ reset: true });
      this.hideCover();

      if (!this.audio.src) {
        const source = this.audio.dataset.src;
        if (!source) return;
        this.audio.src = source;
        this.audio.load();
      }

      try {
        this.audio.muted = false;
        this.audio.currentTime = 0;
        await this.audio.play();
        this.setSoundState(true);
        this.soundButton?.classList.add('show');
        this.soundHideTimer = window.setTimeout(() => {
          this.soundHideTimer = 0;
          this.soundButton?.classList.remove('show');
        }, 800);
      } catch (_error) {
        this.setSoundState(false);
      }
    }

    stopAudio() {
      if (!this.audio) return;
      if (this.soundHideTimer) {
        window.clearTimeout(this.soundHideTimer);
        this.soundHideTimer = 0;
      }
      this.audio.pause();
      this.audio.muted = false;
      try {
        this.audio.currentTime = 0;
      } catch (_error) {}
      this.setSoundState(false);
      this.soundButton?.classList.remove('show');
    }

    pauseAll() {
      this.pauseMedia();
      this.stopAudio();
      this.setHeaderHidden(false);
    }

    requestUpdate() {
      if (this.destroyed || this.frameRequest) return;
      this.frameRequest = window.requestAnimationFrame(() => {
        this.frameRequest = 0;
        this.update();
      });
    }

    update() {
      if (this.destroyed || !this.frame || !this.sticky) return;

      const rect = this.root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const viewportWidth = window.innerWidth || 1;
      const reduced = Boolean(reducedMotion?.matches);
      const denominator = Math.max(1, rect.height - viewportHeight);
      const progress = clamp(-rect.top / denominator);
      const scaleProgress = reduced ? 1 : clamp(progress / 0.3);
      const isMobile = viewportWidth <= 950 || viewportHeight <= 950;
      const isPortrait = viewportHeight > viewportWidth;
      const baseScale = viewportWidth <= 768 ? 0.85 : 0.65;
      let scale = reduced ? 1 : baseScale + (1 - baseScale) * scaleProgress;
      let rotation = 0;

      if (!reduced && isMobile && isPortrait && scaleProgress > 0.8) {
        const rotationProgress = (scaleProgress - 0.8) / 0.2;
        rotation = rotationProgress * 90;
        scale += rotationProgress * (viewportHeight / viewportWidth - 1);
      }

      this.frame.style.transform = reduced
        ? 'none'
        : `scale(${scale}) rotate(${rotation}deg)`;
      if (this.frame.classList.contains('cinematic__frame--stylized')) {
        this.frame.style.borderRadius = reduced ? '0' : `${40 * (1 - scaleProgress)}px`;
      }
      this.sticky.style.backgroundColor = `rgba(0, 0, 0, ${scaleProgress})`;

      const shouldPlay = reduced
        ? this.visible
        : rect.top <= 0 && rect.bottom > viewportHeight;

      if (shouldPlay && this.nearViewport) {
        if (!this.mediaStarted) {
          this.mediaStarted = true;
          void this.ensureMedia().then(() => {
            if (!this.mediaStarted || !this.visible) return;
            this.playMedia();
          });
        } else {
          this.playMedia();
        }
      } else if (!shouldPlay && this.mediaStarted && rect.top > 0) {
        this.mediaStarted = false;
        this.showCover();
        this.resetMedia();
      } else if (!this.visible) {
        this.pauseMedia();
      }

      const soundAvailable = Boolean(this.audio?.dataset.src);
      const shouldShowSound = soundAvailable && (reduced ? this.visible : scaleProgress > 0.95);
      if (this.soundActive) {
        this.soundButton?.classList.remove('show');
      } else {
        this.soundButton?.classList.toggle('show', shouldShowSound);
      }

      const hideHeader =
        !reduced && scaleProgress > 0.4 && rect.top <= 0 && rect.bottom > viewportHeight;
      this.setHeaderHidden(hideHeader);

      if (
        (progress <= 0 || progress >= 1 || !this.visible) &&
        this.audio &&
        !this.audio.paused
      ) {
        this.stopAudio();
      }
    }

    destroy() {
      this.destroyed = true;
      if (this.frameRequest) window.cancelAnimationFrame(this.frameRequest);
      if (this.coverRevealTimer) window.clearTimeout(this.coverRevealTimer);
      if (this.soundHideTimer) window.clearTimeout(this.soundHideTimer);
      this.abortController.abort();
      this.preloadObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.pauseAll();
      activeControllers.delete(this);
      controllers.delete(this.root);
    }
  }

  const getRoots = (root = document) => {
    const roots = [];
    if (root.matches?.(ROOT_SELECTOR)) roots.push(root);
    root.querySelectorAll?.(ROOT_SELECTOR).forEach((element) => roots.push(element));
    return roots;
  };

  const init = (root = document) => {
    getRoots(root).forEach((element) => {
      if (controllers.has(element)) return;
      const controller = new CinematicVideo(element);
      controllers.set(element, controller);
    });
  };

  const destroy = (root) => {
    getRoots(root).forEach((element) => controllers.get(element)?.destroy());
  };

  window[GLOBAL_KEY] = { init, destroy };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) activeControllers.forEach((controller) => controller.pauseAll());
    else activeControllers.forEach((controller) => controller.requestUpdate());
  });

  init(document);
})();
